import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DOMPurify from 'dompurify';
import { useAssociation } from './contexts/AssociationContext';
import TeacherLogin from './components/LoginPages/TeacherLogin';
import AdminLogin from './components/LoginPages/AdminLogin';
import ParentLogin from './components/LoginPages/ParentLogin';
import AdminDashboard from './components/AdminDashboard';
import TeacherDashboard from './components/TeacherDashboard';
import ParentDashboard from './components/ParentDashboard';
import IconTestPage from './components/IconTestPage';
import InscriptionPage from './components/InscriptionPage';
// import MaintenancePage from './components/MaintenancePage';
import { 
  adminDashboardTranslations as importedAdminDashboardTranslations,
  homeTranslations,
  parentDashboardTranslations as importedParentDashboardTranslations,
  loginTranslations as importedLoginTranslations,
  hadithTranslations as importedHadithTranslations
} from './locales';
import { ApiServiceShared } from './services/ApiServiceShared';
import { ENV_CONFIG } from './config/environment';

interface LandingSection {
  id: number;
  section_type: 'about' | 'project' | 'service' | 'event';
  lang: 'fr' | 'ar' | 'en';
  sort_order: number;
  title: string | null;
  description: string | null;
  detail_html: string | null;
  image_url: string | null;
  is_visible: 0 | 1;
}

const App: React.FC = () => {
  const { getName, getSubtitle, association } = useAssociation();
  const theme        = association?.theme;
  const primaryColor = theme?.primary     ?? association?.primaryColor ?? '#2E7D32';
  const primaryText  = theme?.primaryText ?? '#FFFFFF';
  const headerBg     = theme?.headerBg    ?? primaryColor;
  const headerText   = theme?.headerText  ?? '#FFFFFF';
  const accent       = theme?.accent      ?? primaryColor;
  const surface      = theme?.surface     ?? '#E8F5E9';
  const logoUrl = association?.logoUrl ?? '/images/logo.png';

  // Vérifier l'accès maintenance avec expiration de 1 heure
  // const checkMaintenanceAccess = (): boolean => {
  //   const access = localStorage.getItem('maintenance_access');
  //   const timestamp = localStorage.getItem('maintenance_timestamp');
  //   
  //   if (!access || !timestamp) {
  //     return false;
  //   }
  //   
  //   // Vérifier si moins d'1 heure s'est écoulée (3600000 ms = 1 heure)
  //   const now = Date.now();
  //   const savedTime = parseInt(timestamp, 10);
  //   const oneHour = 60 * 60 * 1000; // 1 heure en millisecondes
  //   
  //   if (now - savedTime > oneHour) {
  //     // Expiration : nettoyer et retourner false
  //   localStorage.removeItem('maintenance_access');
  //   localStorage.removeItem('maintenance_timestamp');
  //     return false;
  //   }
  //   
  //   return true;
  // };
  
  // Vérifier l'accès au chargement
  // const [hasMaintenanceAccess, setHasMaintenanceAccess] = useState(checkMaintenanceAccess());
  
  // Vérifier l'accès au chargement et restaurer si valide
  // useEffect(() => {
  //   const hasAccess = checkMaintenanceAccess();
  //   if (hasAccess) {
  //     setHasMaintenanceAccess(true);
  //   }
  // }, []);

  // Vérifier les tokens de login avec expiration de 1 heure
  const checkLoginToken = (): string | null => {
    const token = localStorage.getItem('app_token');
    const userStr = localStorage.getItem('app_user');
    const loginTimestamp = localStorage.getItem('login_timestamp');
    
    if (!token || !userStr || !loginTimestamp) {
      return null;
    }
    
    // Vérifier si moins d'1 heure s'est écoulée (3600000 ms = 1 heure)
    const now = Date.now();
    const savedTime = parseInt(loginTimestamp, 10);
    const oneHour = 60 * 60 * 1000; // 1 heure en millisecondes
    
    if (now - savedTime > oneHour) {
      // Expiration : nettoyer et retourner null
      localStorage.removeItem('app_token');
      localStorage.removeItem('app_user');
      localStorage.removeItem('login_timestamp');
      return null;
    }
    
    // Vérifier le type d'utilisateur pour rediriger vers le bon dashboard
    try {
      const user = JSON.parse(userStr);
      const userType = user.type || user.user_type;
      
      if (userType === 'admin') {
        return 'admin-dashboard';
      } else if (userType === 'teacher') {
        return 'teacher-dashboard';
      } else if (userType === 'parent') {
        return 'parent-dashboard';
      }
    } catch (error) {
      console.error('Erreur lors du parsing de l\'utilisateur:', error);
    }
    
    return null;
  };
  
  // Vérifier le token au chargement et rediriger si valide
  useEffect(() => {
    const dashboardPage = checkLoginToken();
    if (dashboardPage) {
      setCurrentPage(dashboardPage);
    }
  }, []);

  const [currentLanguage, setCurrentLanguage] = useState<string>(() => {
    try {
      const saved = localStorage.getItem('selectedLanguage');
      return (saved && ['ar', 'fr', 'en'].includes(saved)) ? saved : 'ar';
    } catch {
      return 'ar';
    }
  });
  const [isRTL, setIsRTL] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('selectedLanguage');
      return saved !== 'fr' && saved !== 'en';
    } catch {
      return true;
    }
  });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [currentPage, setCurrentPage] = useState('home');

  // Contact modal
  const [showContactModal, setShowContactModal] = useState(false);

  // Foundation modals
  const [showFoundationInfo, setShowFoundationInfo] = useState(false);
  const [showFoundationCompose, setShowFoundationCompose] = useState(false);
  const [composeName, setComposeName] = useState('');
  const [composeEmail, setComposeEmail] = useState('');
  const [composePhone, setComposePhone] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeMessage, setComposeMessage] = useState('');

  // Contact public form state
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactSubject, setContactSubject] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [contactLoading, setContactLoading] = useState(false);
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [contactSubmittedId, setContactSubmittedId] = useState<number | null>(null);
  const [contactError, setContactError] = useState('');

  
  // État pour les statistiques
  const [statistics, setStatistics] = useState({
    students: 1250,
    teachers: 45,
    circles: 12,
    parents: 890
  });

  // État pour les sections paramétrables de la page d'accueil
  const [landingSections, setLandingSections] = useState<LandingSection[]>([]);
  // true une fois que l'API a répondu (même si elle retourne 0 résultats)
  const [landingLoaded, setLandingLoaded] = useState(false);

  // État pour la modal "Lire la suite"
  const [modalContent, setModalContent] = useState<{ title: string; html: string } | null>(null);

  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = currentLanguage;
  }, [isRTL, currentLanguage]);

  // Langue par défaut de l'association si l'utilisateur n'a pas encore choisi
  useEffect(() => {
    const def = association?.defaultLanguage;
    if (!def || !['ar', 'fr', 'en'].includes(def)) return;
    try {
      const saved = localStorage.getItem('selectedLanguage');
      if (!saved) {
        setCurrentLanguage(def);
        setIsRTL(def === 'ar');
      }
    } catch {
      /* ignore */
    }
  }, [association?.defaultLanguage]);

  // Après enregistrement dans الإعدادات → المعلومات
  useEffect(() => {
    const onLang = (e: Event) => {
      const lang = (e as CustomEvent<string>).detail;
      if (lang && ['ar', 'fr', 'en'].includes(lang)) {
        setCurrentLanguage(lang);
        setIsRTL(lang === 'ar');
      }
    };
    window.addEventListener('tanzeel-language-changed', onLang);
    return () => window.removeEventListener('tanzeel-language-changed', onLang);
  }, []);

  // Sauvegarder la langue choisie
  useEffect(() => {
    localStorage.setItem('selectedLanguage', currentLanguage);
  }, [currentLanguage]);

  // Charger les statistiques et les sections de la page d'accueil depuis le backend
  useEffect(() => {
    const loadStatistics = async () => {
      try {
        const result = await ApiServiceShared.getPublicStatistics();
        if (result.success && result.data) {
          setStatistics({
            students: result.data.students || 1250,
            teachers: result.data.teachers || 45,
            circles: result.data.circles || 12,
            parents: result.data.parents || 890
          });
        }
      } catch (error) {
        console.error('Erreur lors du chargement des statistiques:', error);
      }
    };

    const loadLandingSections = async () => {
      try {
        const response = await fetch(`${ENV_CONFIG.apiBaseUrl}/api/public/landing-sections`);
        const result = await response.json();
        if (result.success && Array.isArray(result.data)) {
          setLandingSections(result.data);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des sections landing:', error);
      } finally {
        setLandingLoaded(true);
      }
    };

    loadStatistics();
    loadLandingSections();
  }, []);

  // Fermer la modal avec la touche Echap
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setModalContent(null);
    };
    if (modalContent) window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [modalContent]);

  // Helpers pour accéder aux sections par type et langue
  const getLandingAbout = useCallback((lang: string) =>
    landingSections.find(s => s.section_type === 'about' && s.lang === lang && s.is_visible),
  [landingSections]);

  // sort_order === 0 → titre de section ; sort_order >= 1 → items/cartes
  const getSectionTitle = useCallback((type: 'project' | 'service', lang: string, fallback: string) => {
    if (!landingLoaded) return fallback;
    const header = landingSections.find(
      s => s.section_type === type && s.lang === lang && s.sort_order === 0
    );
    return header?.title || fallback;
  }, [landingSections, landingLoaded]);

  const getLandingItems = useCallback((type: 'project' | 'service', lang: string) =>
    landingSections
      .filter(s => s.section_type === type && s.lang === lang && s.is_visible && s.sort_order > 0)
      .sort((a, b) => a.sort_order - b.sort_order),
  [landingSections]);

  // Événements visibles pour la langue courante (sort_order > 0)
  const getLandingEvents = useCallback((lang: string) =>
    landingSections
      .filter(s => s.section_type === 'event' && s.lang === lang && s.is_visible && s.sort_order > 0)
      .sort((a, b) => a.sort_order - b.sort_order),
  [landingSections]);

  const resolveImageUrl = useCallback((url: string | null) => {
    if (!url) return null;
    if (url.startsWith('/uploads')) return `${ENV_CONFIG.apiBaseUrl}${url}`;
    return url;
  }, []);

  // Config section événements (sort_order=0) → fond global
  const getEventSectionBg = useCallback((lang: string): React.CSSProperties => {
    const cfg = landingSections.find(s => s.section_type === 'event' && s.lang === lang && s.sort_order === 0);
    const raw = cfg?.image_url;
    if (!raw) return {};
    return /^(#|rgb|rgba|hsl|linear-gradient|radial-gradient)/i.test(raw)
      ? { background: raw }
      : { backgroundImage: `url(${resolveImageUrl(raw)})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' };
  }, [landingSections, resolveImageUrl]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Images rotatives pour la section About
  const aboutImages = useMemo(() => [
    '/images/about_01.jpg',
    '/images/about_02.jpg',
    '/images/about_03.jpg',
    '/images/about_04.jpg'
  ], []);

  // Hadiths du Prophète avec traductions - Un hadith par image
  const hadithTranslations = importedHadithTranslations;

  // Traductions pour le Parent Dashboard (inclut aussi les traductions du Teacher Dashboard)
  const parentDashboardTranslations = importedParentDashboardTranslations;


  // Fonction pour gérer les erreurs d'authentification
  const handleAuthError = () => {
    // Nettoyer le localStorage
    localStorage.removeItem('app_token');
    localStorage.removeItem('app_user');
    localStorage.removeItem('login_timestamp');
    
    // Rediriger vers la page d'accueil sans recharger
    setCurrentPage('home');
  };

  const handleContactSubmit = async () => {
    setContactError('');
    if (!contactName.trim()) { setContactError(t('contactErrName')); return; }
    if (!contactSubject.trim()) { setContactError(t('contactErrSubject')); return; }
    if (!contactMessage.trim() || contactMessage.trim().length < 10) { setContactError(t('contactErrMsg')); return; }
    if (!contactPhone.trim() && !contactEmail.trim()) { setContactError(t('contactErrPhoneOrEmail')); return; }
    setContactLoading(true);
    try {
      const res = await ApiServiceShared.submitContact({
        full_name: contactName.trim(),
        phone: contactPhone.trim() || undefined,
        email: contactEmail.trim() || undefined,
        subject: contactSubject.trim(),
        message: contactMessage.trim(),
      });
      if (res.success) {
        setContactSubmittedId(res.data?.id ?? null);
        setContactSubmitted(true);
      } else {
        setContactError(res.statusCode === 429 ? t('contactErrLimit') : t('contactErrGeneric'));
      }
    } finally {
      setContactLoading(false);
    }
  };

  const handleSendFoundationEmail = () => {
    if (!composeName.trim() || !composeMessage.trim()) {
      alert(t('foundationErrRequired'));
      return;
    }
    const subject = encodeURIComponent(composeSubject.trim() || t('foundationDefaultSubject'));
    const contactInfo = [
      composeEmail.trim() ? `Email : ${composeEmail.trim()}` : '',
      composePhone.trim() ? `Tél : ${composePhone.trim()}` : '',
    ].filter(Boolean).join('\n');
    const body = encodeURIComponent(
      `De : ${composeName.trim()}${contactInfo ? '\n' + contactInfo : ''}\n\n${composeMessage.trim()}`
    );
    window.location.href = `mailto:${t('foundationEmail')}?subject=${subject}&body=${body}`;
    setShowFoundationCompose(false);
    setComposeName(''); setComposeEmail(''); setComposePhone('');
    setComposeSubject(''); setComposeMessage('');
  };

  const resetContactForm = () => {
    setContactName(''); setContactPhone(''); setContactEmail('');
    setContactSubject(''); setContactMessage('');
    setContactSubmitted(false); setContactSubmittedId(null); setContactError('');
    setShowContactModal(false);
  };

  // Traductions pour le Admin Dashboard - Fusion des traductions importées avec les traductions existantes
  const adminDashboardTranslations = {
    ar: {
      ...importedAdminDashboardTranslations.ar,
      // Communication section (additional)
      sentMessages: 'الرسائل المرسلة',
      noSentMessages: 'لا توجد رسائل مرسلة بعد',
      recipient: 'مستقبل',
      recipients: 'مستقبلين',
      messageTypeGeneral: 'عام',
      messageTypeAbsence: 'غياب',
      messageTypeMeeting: 'اجتماع',
      messageTypeCongratulation: 'تهنئة',
      messageTypeAnnouncement: 'إعلان',
      messageTypeWarning: 'تحذير',
      reportTypeAssignment: 'واجب منزلي',
      reportTypeProgress: 'تقرير تقدم',
      reportTypeAchievement: 'إنجاز متميز',
      reportTypeConcern: 'ملاحظة مهمة',
    },
    fr: {
      ...importedAdminDashboardTranslations.fr,
      // Communication section (additional)
      sentMessages: 'Messages Envoyés',
      noSentMessages: 'Aucun message envoyé',
      recipient: 'destinataire',
      recipients: 'destinataires',
      messageTypeGeneral: 'Général',
      messageTypeAbsence: 'Absence',
      messageTypeMeeting: 'Réunion',
      messageTypeCongratulation: 'Félicitation',
      messageTypeAnnouncement: 'Annonce',
      messageTypeWarning: 'Avertissement',
      reportTypeAssignment: 'Devoir',
      reportTypeProgress: 'Rapport de Progrès',
      reportTypeAchievement: 'Réussite Exceptionnelle',
      reportTypeConcern: 'Note Importante',
    },
    en: {
      ...importedAdminDashboardTranslations.en,
      // Communication section (additional)
      sentMessages: 'Sent Messages',
      noSentMessages: 'No messages sent yet',
      recipient: 'recipient',
      recipients: 'recipients',
      messageTypeGeneral: 'General',
      messageTypeAbsence: 'Absence',
      messageTypeMeeting: 'Meeting',
      messageTypeCongratulation: 'Congratulation',
      messageTypeAnnouncement: 'Announcement',
      messageTypeWarning: 'Warning',
      reportTypeAssignment: 'Homework',
      reportTypeProgress: 'Progress Report',
      reportTypeAchievement: 'Outstanding Achievement',
      reportTypeConcern: 'Important Note',
    }
  };

  // Traductions pour les pages de connexion
  const loginTranslations = importedLoginTranslations;

  // Préchargement des images pour une transition plus fluide
  useEffect(() => {
    aboutImages.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, [aboutImages]);

  // Rotation automatique des images avec transition fluide
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isTransitioning) {
        setIsTransitioning(true);
        setTimeout(() => {
          setCurrentImageIndex((prevIndex) => 
            (prevIndex + 1) % aboutImages.length
          );
          setTimeout(() => {
            setIsTransitioning(false);
          }, 100);
        }, 200);
      }
    }, 5000); // Change toutes les 5 secondes pour plus de fluidité

    return () => clearInterval(interval);
  }, [aboutImages.length, isTransitioning]);

  const changeLanguage = (lang: string) => {
    setCurrentLanguage(lang);
    setIsRTL(lang === 'ar');
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const navigateToPage = (page: string) => {
    setCurrentPage(page);
    setIsMenuOpen(false);
  };

  // const goHome = () => {
  //   setCurrentPage('home');
  // };

  // Traductions pour la page d'accueil
  const translations = homeTranslations;

  const t = (key: string) => {
    const langTranslations = translations[currentLanguage as keyof typeof translations];
    return (langTranslations as { [key: string]: string })?.[key] || key;
  };

  // Fonction de traduction pour les pages de connexion
  // const tLogin = (key: string) => {
  //   return (loginTranslations[currentLanguage as keyof typeof loginTranslations] as any)?.[key] || key;
  // };

  const StatItem: React.FC<{ target: number; label: string }> = ({ target, label }) => {
    const [count, setCount] = useState(0);
    const [hasAnimated, setHasAnimated] = useState(false);

    useEffect(() => {
      if (!hasAnimated) {
        const timer = setTimeout(() => {
          let current = 0;
          const increment = target / 100;
          const interval = setInterval(() => {
            current += increment;
            if (current >= target) {
              current = target;
              clearInterval(interval);
              setHasAnimated(true);
            }
            setCount(Math.floor(current));
          }, 20);
        }, 500);
        return () => clearTimeout(timer);
      }
    }, [target, hasAnimated]);

    return (
      <motion.div
        style={{
          textAlign: 'center',
          padding: windowWidth < 768 ? '15px' : '20px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        }}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div style={{ 
          fontSize: windowWidth < 768 ? '2rem' : '2.5rem', 
          fontWeight: 'bold', 
          color: primaryColor 
        }}>
          {count.toLocaleString(currentLanguage === 'ar' ? 'ar-SA' : 'en-US')}
        </div>
        <div style={{ 
          fontSize: windowWidth < 768 ? '0.9rem' : '1.1rem', 
          color: '#6C757D' 
        }}>
          {label}
        </div>
      </motion.div>
    );
  };

  // Gestion de la page de maintenance
  // if (!hasMaintenanceAccess) {
  //   return (
  //     <MaintenancePage 
  //       onAccessGranted={() => setHasMaintenanceAccess(true)}
  //     />
  //   );
  // }

  // Gestion des pages
  if (currentPage === 'teacher-login') {
    return <TeacherLogin 
      translations={loginTranslations} 
      currentLanguage={currentLanguage} 
      isRTL={isRTL}
      onBackHome={() => setCurrentPage('home')}
      onLoginSuccess={() => setCurrentPage('teacher-dashboard')}
    />;
  }
  
  if (currentPage === 'admin-login') {
    return <AdminLogin 
      translations={loginTranslations} 
      currentLanguage={currentLanguage} 
      isRTL={isRTL}
      onBackHome={() => setCurrentPage('home')}
      onLoginSuccess={() => setCurrentPage('admin-dashboard')}
    />;
  }
  
  if (currentPage === 'parent-login') {
    return <ParentLogin 
      translations={loginTranslations} 
      currentLanguage={currentLanguage} 
      isRTL={isRTL}
      onBackHome={() => setCurrentPage('home')}
      onLoginSuccess={() => setCurrentPage('parent-dashboard')}
    />;
  }


  


      if (currentPage === 'admin-dashboard') {
        return <AdminDashboard
          onBackHome={() => setCurrentPage('home')}
          onAuthError={handleAuthError}
          currentLanguage={currentLanguage}
          translations={adminDashboardTranslations}
        />;
      }

      if (currentPage === 'teacher-dashboard') {
        return <TeacherDashboard 
          onBackHome={() => setCurrentPage('home')}
          onAuthError={handleAuthError}
          currentLanguage={currentLanguage}
          translations={parentDashboardTranslations}
        />;
      }

      if (currentPage === 'parent-dashboard') {
        return <ParentDashboard 
          onBackHome={() => setCurrentPage('home')} 
          onAuthError={handleAuthError}
          currentLanguage={currentLanguage}
          translations={parentDashboardTranslations}
        />;
      }

      if (currentPage === 'inscription') {
    return <InscriptionPage
      isRTL={isRTL}
      t={t}
      onBack={() => setCurrentPage('home')}
        />;
      }

      if (currentPage === 'icon-test') {
        return (
          <div>
            <div style={{
              backgroundColor: '#3b82f6',
              padding: '15px 20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <button
                onClick={() => setCurrentPage('home')}
                style={{
                  backgroundColor: 'white',
                  color: '#3b82f6',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '600'
                }}
              >
                ← العودة للصفحة الرئيسية
              </button>
              <h2 style={{ color: 'white', margin: 0 }}>معرض الأيقونات</h2>
            </div>
            <IconTestPage />
          </div>
        );
      }



  return (
    <div style={{ 
      fontFamily: 'Cairo, sans-serif', 
      direction: isRTL ? 'rtl' : 'ltr', 
      minHeight: '100vh', 
      color: '#2C3E50', 
      backgroundColor: '#FFFFFF',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <motion.header
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          background: headerBg,
          boxShadow: '0 2px 20px rgba(0,0,0,0.1)',
          zIndex: 1000,
          padding: windowWidth < 768 ? '15px 20px' : '20px 40px',
        }}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: windowWidth < 768 ? 'wrap' : 'nowrap'
        }}>
          {/* Section gauche : Logo + Nom + Menu */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: windowWidth < 768 ? '12px' : '30px',
            flexWrap: windowWidth < 768 ? 'wrap' : 'nowrap',
            flex: 1,
            minWidth: 0
          }}>
            {/* Logo + Nom */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: windowWidth < 768 ? '8px' : '15px',
              flexShrink: 0,
              minWidth: 0
            }}>
              <img 
                src={logoUrl} 
                alt={getName(currentLanguage)} 
                style={{ 
                  width: windowWidth < 768 ? '35px' : '50px', 
                  height: windowWidth < 768 ? '35px' : '50px', 
                  borderRadius: '50%', 
                  objectFit: 'cover',
                  flexShrink: 0
                }}
              />
              <div style={{ 
                fontSize: windowWidth < 768 ? '1rem' : '1.8rem', 
                fontWeight: 'bold', 
                color: headerText,
                whiteSpace: windowWidth < 768 ? 'normal' : 'nowrap',
                lineHeight: windowWidth < 768 ? '1.3' : '1.2',
                wordBreak: windowWidth < 768 ? 'normal' : 'keep-all',
                overflow: 'visible'
              }}>
                {getName(currentLanguage)}
              </div>
            </div>
            
            {/* Menu de navigation */}
            <nav style={{ 
              display: windowWidth < 768 ? 'none' : 'flex', 
              alignItems: 'center' 
            }}>
              <ul style={{ 
                listStyle: 'none', 
                display: 'flex', 
                margin: 0, 
                padding: 0,
                flexWrap: 'wrap',
                gap: windowWidth < 1024 ? '10px' : '15px'
              }}>
                {['home', 'about', 'projects', 'statistics', 'services', 'contact'].map((item) => (
                  <li key={item} style={{ margin: '0 10px' }}>
                    <a href={`#${item}`} style={{ 
                      textDecoration: 'none', 
                      color: headerText, 
                      fontSize: '18px',
                      fontWeight: '500',
                      transition: 'color 0.3s ease',
                      opacity: 0.9
                    }}>
                      {t(item)}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* Section droite : Sélecteur de langue */}
          <div style={{ 
            display: windowWidth < 768 ? 'none' : 'flex', 
            alignItems: 'center' 
          }}>
            <select
              onChange={(e) => changeLanguage(e.target.value)}
              value={currentLanguage}
              style={{
                padding: '8px 12px',
                borderRadius: '5px',
                border: `1px solid rgba(255,255,255,0.4)`,
                backgroundColor: 'rgba(255,255,255,0.15)',
                color: headerText,
                fontSize: windowWidth < 768 ? '0.8rem' : '1rem',
                cursor: 'pointer'
              }}
            >
              <option value="ar" style={{ color: '#1f2937', backgroundColor: '#ffffff' }}>العربية</option>
              <option value="en" style={{ color: '#1f2937', backgroundColor: '#ffffff' }}>English</option>
              <option value="fr" style={{ color: '#1f2937', backgroundColor: '#ffffff' }}>Français</option>
            </select>
          </div>
          {/* Mobile Menu Button */}
          <button
            style={{
              display: windowWidth < 768 ? 'block' : 'none',
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: headerText
            }}
            onClick={toggleMenu}
          >
            ☰
          </button>
        </div>
        {/* Mobile Menu */}
        {isMenuOpen && (
          <div style={{
            display: windowWidth < 768 ? 'block' : 'none',
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: 'rgba(255, 255, 255, 0.98)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            padding: '20px'
          }}>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {['home', 'about', 'projects', 'statistics', 'services', 'contact'].map((item) => (
                <li key={item} style={{ marginBottom: '15px' }}>
                  <a 
                    href={`#${item}`} 
                    style={{ 
                      textDecoration: 'none', 
                      color: '#2C3E50', 
                      fontSize: '18px',
                      fontWeight: '500',
                      display: 'block',
                      padding: '10px 0'
                    }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t(item)}
                  </a>
                </li>
              ))}
            </ul>
            <div style={{ marginTop: '20px' }}>
              <select
                onChange={(e) => changeLanguage(e.target.value)}
                value={currentLanguage}
                style={{ 
                  padding: '10px 15px', 
                  borderRadius: '5px', 
                  border: '1px solid #E9ECEF', 
                  backgroundColor: '#F8F9FA',
                  width: '100%'
                }}
              >
                <option value="ar">العربية</option>
                <option value="en">English</option>
                <option value="fr">Français</option>
              </select>
            </div>
          </div>
        )}
      </motion.header>

      {/* Hero Section */}
      <section id="home" style={{ 
        padding: windowWidth < 768 ? '100px 20px' : '120px 40px', 
        textAlign: 'center', 
        backgroundColor: surface, 
        color: '#2C3E50' 
      }}>
        <motion.h1
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          style={{ 
            fontSize: windowWidth < 768 ? '2rem' : windowWidth < 1024 ? '2.5rem' : '3rem', 
            marginBottom: '30px', 
            color: theme?.primary ?? primaryColor,
            lineHeight: '1.3',
            fontWeight: 'bold'
          }}
        >
          {getSubtitle(currentLanguage) || t('heroTitle')}
        </motion.h1>
        
        <motion.p
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          style={{ 
            fontSize: windowWidth < 768 ? '1.1rem' : '1.5rem', 
            marginBottom: '60px', 
            maxWidth: windowWidth < 768 ? '100%' : '800px', 
            margin: '0 auto 60px',
            padding: windowWidth < 768 ? '0 10px' : '0',
            lineHeight: '1.6',
            color: '#4A5568'
          }}
        >
          {t('heroSubtitle')}
        </motion.p>
        
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: windowWidth < 768 ? '15px' : '25px', 
          flexWrap: 'wrap',
          flexDirection: windowWidth < 480 ? 'column' : 'row',
          alignItems: 'center',
          marginTop: '20px'
        }}>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigateToPage('admin-login')}
            style={{
              padding: windowWidth < 768 ? '15px 25px' : '18px 35px',
              fontSize: windowWidth < 768 ? '1rem' : '1.2rem',
              borderRadius: '10px',
              border: 'none',
              backgroundColor: primaryColor,
              color: primaryText,
              cursor: 'pointer',
              boxShadow: '0 6px 20px rgba(46, 125, 50, 0.3)',
              width: windowWidth < 480 ? '100%' : 'auto',
              maxWidth: windowWidth < 480 ? '300px' : 'none',
              fontWeight: '600',
              transition: 'all 0.3s ease'
            }}
          >
            {t('adminLogin')}
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigateToPage('teacher-login')}
            style={{
              padding: windowWidth < 768 ? '15px 25px' : '18px 35px',
              fontSize: windowWidth < 768 ? '1rem' : '1.2rem',
              borderRadius: '10px',
              border: `2px solid ${primaryColor}`,
              backgroundColor: 'transparent',
              color: primaryColor,
              cursor: 'pointer',
              boxShadow: '0 6px 20px rgba(46, 125, 50, 0.1)',
              width: windowWidth < 480 ? '100%' : 'auto',
              maxWidth: windowWidth < 480 ? '300px' : 'none',
              fontWeight: '600',
              transition: 'all 0.3s ease'
            }}
          >
            {t('teacherLogin')}
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigateToPage('parent-login')}
            style={{
              padding: windowWidth < 768 ? '15px 25px' : '18px 35px',
              fontSize: windowWidth < 768 ? '1rem' : '1.2rem',
              borderRadius: '10px',
              border: `2px solid ${accent}`,
              backgroundColor: 'transparent',
              color: accent,
              cursor: 'pointer',
              boxShadow: '0 6px 20px rgba(76, 175, 80, 0.1)',
              width: windowWidth < 480 ? '100%' : 'auto',
              maxWidth: windowWidth < 480 ? '300px' : 'none',
              fontWeight: '600',
              transition: 'all 0.3s ease'
            }}
          >
            {t('parentLogin')}
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigateToPage('inscription')}
            style={{
              padding: windowWidth < 768 ? '15px 25px' : '18px 35px',
              fontSize: windowWidth < 768 ? '1rem' : '1.2rem',
              borderRadius: '10px',
              border: 'none',
              backgroundColor: '#F59E0B',
              color: '#FFF',
              cursor: 'pointer',
              boxShadow: '0 6px 20px rgba(245, 158, 11, 0.3)',
              width: windowWidth < 480 ? '100%' : 'auto',
              maxWidth: windowWidth < 480 ? '300px' : 'none',
              fontWeight: '600',
              transition: 'all 0.3s ease'
            }}
          >
            {t('inscriptionBtn')}
          </motion.button>
          
        </div>

      </section>

      {/* About Section */}
      <section id="about" style={{ 
        padding: windowWidth < 768 ? '60px 20px' : '80px 40px', 
        backgroundColor: '#FFFFFF', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: windowWidth < 768 ? '30px' : '50px',
        flexDirection: windowWidth < 768 ? 'column' : 'row'
      }}>
        <motion.div
          initial={{ opacity: 0, x: windowWidth < 768 ? 0 : -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          style={{ 
            flex: 1, 
            maxWidth: windowWidth < 768 ? '100%' : '600px',
            order: windowWidth < 768 ? 2 : 1
          }}
        >
            <h2 style={{ 
              fontSize: windowWidth < 768 ? '2rem' : '2.5rem', 
              color: primaryColor, 
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              {(landingLoaded ? getLandingAbout(currentLanguage)?.title : null) || t('aboutTitle')}
            </h2>
            <div
              style={{ fontSize: windowWidth < 768 ? '1rem' : '1.1rem', lineHeight: '1.8', color: '#6C757D', textAlign: 'center' }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(
                (landingLoaded ? getLandingAbout(currentLanguage)?.description : null) || t('aboutText')
              )}}
            />
            {getLandingAbout(currentLanguage)?.detail_html && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                const section = getLandingAbout(currentLanguage);
                if (section?.detail_html) {
                  setModalContent({ title: section.title || t('aboutTitle'), html: section.detail_html });
                }
              }}
              style={{
                marginTop: '30px',
                padding: '12px 25px',
                fontSize: '1rem',
                borderRadius: '5px',
                border: `2px solid ${primaryColor}`,
                backgroundColor: 'transparent',
                color: primaryColor,
                cursor: 'pointer',
                display: 'block',
                margin: '30px auto 0'
              }}
            >
              {t('readMore')}
            </motion.button>
            )}
        </motion.div>
          <motion.div
            initial={{ opacity: 0, x: windowWidth < 768 ? 0 : 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            style={{ 
              flex: 1, 
              maxWidth: windowWidth < 768 ? '100%' : '500px',
              order: windowWidth < 768 ? 1 : 2,
              position: 'relative',
              width: '100%',
              minWidth: 0
            }}
          >
            <div style={{
              position: 'relative',
              borderRadius: windowWidth < 768 ? '12px' : '15px',
              overflow: 'hidden',
              boxShadow: '0 15px 35px rgba(0,0,0,0.15)',
              minHeight: windowWidth < 768 ? '250px' : '300px',
              height: windowWidth < 768 ? '250px' : '300px',
              width: '100%',
              backgroundColor: '#f0f0f0'
            }}>
              {/* Image avec transition ultra-fluide */}
              <motion.div
                key={currentImageIndex}
                initial={{ opacity: windowWidth < 768 ? 1 : 0, scale: windowWidth < 768 ? 1 : 1.05, filter: windowWidth < 768 ? 'blur(0px)' : 'blur(8px)' }}
                animate={{ 
                  opacity: 1, 
                  scale: 1, 
                  filter: 'blur(0px)',
                  transition: { 
                    duration: windowWidth < 768 ? 0.5 : 2,
                    ease: [0.23, 1, 0.32, 1] // easeOutQuart pour plus de fluidité
                  }
                }}
                exit={{ 
                  opacity: 0, 
                  scale: 0.95, 
                  filter: 'blur(4px)',
                  transition: { 
                    duration: windowWidth < 768 ? 0.3 : 1.2,
                    ease: [0.23, 1, 0.32, 1]
                  }
                }}
                style={{ 
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  zIndex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <img 
                  src={aboutImages[currentImageIndex]} 
                  alt="About Us" 
                  style={{ 
                    width: '100%', 
                    height: '100%',
                    minHeight: windowWidth < 768 ? '250px' : '300px',
                    maxHeight: windowWidth < 768 ? '250px' : '300px',
                    objectFit: 'cover',
                    objectPosition: 'center',
                    display: 'block',
                    flexShrink: 0
                  }} 
                  onError={(e) => {
                    console.error('Erreur de chargement de l\'image:', aboutImages[currentImageIndex]);
                    (e.target as HTMLImageElement).style.display = 'none';
                  }} 
                />
              </motion.div>
            
            {/* Overlay avec le hadith */}
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              background: 'linear-gradient(transparent, rgba(0,0,0,0.9))',
              padding: windowWidth < 768 ? '15px 12px' : '25px 20px',
              color: 'white',
              zIndex: 2
            }}>
              <motion.div
                key={`hadith-${currentImageIndex}`}
                initial={{ opacity: windowWidth < 768 ? 1 : 0, y: windowWidth < 768 ? 0 : 40, scale: windowWidth < 768 ? 1 : 0.95 }}
                animate={{ 
                  opacity: 1, 
                  y: 0, 
                  scale: 1,
                  transition: { 
                    duration: windowWidth < 768 ? 0.3 : 1.2, 
                    delay: windowWidth < 768 ? 0 : 0.6,
                    ease: [0.23, 1, 0.32, 1]
                  }
                }}
                exit={{ 
                  opacity: 0, 
                  y: -30, 
                  scale: 0.9,
                  transition: { 
                    duration: windowWidth < 768 ? 0.2 : 0.8,
                    ease: [0.23, 1, 0.32, 1]
                  }
                }}
                style={{
                  textAlign: 'center',
                  zIndex: 2,
                  position: 'relative'
                }}
              >
                {/* Référence au Prophète */}
                <p style={{
                  fontSize: windowWidth < 768 ? '0.7rem' : '0.8rem',
                  lineHeight: '1.4',
                  margin: '0 0 8px 0',
                  fontWeight: '400',
                  color: 'rgba(255,255,255,0.8)',
                  fontStyle: 'italic'
                }}>
                  {(() => {
                    const hadith = hadithTranslations[currentImageIndex];
                    if (!hadith) return "قال رسول الله صلى الله عليه وسلم:";
                    const lang = currentLanguage as 'ar' | 'fr' | 'en';
                    return hadith[lang]?.reference || hadith.ar.reference;
                  })()}
                </p>
                
                {/* Hadith principal */}
                <p style={{
                  fontSize: windowWidth < 768 ? '1.1rem' : '1.4rem',
                  lineHeight: '1.8',
                  margin: 0,
                  fontWeight: '600',
                  color: '#FFFFFF',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                  fontFamily: 'Cairo, sans-serif'
                }}>
                  {(() => {
                    const hadith = hadithTranslations[currentImageIndex];
                    if (!hadith) return "من علمَ آيةً من كتابِ اللهِ عز وجل كانَ لهُ ثوابُها ما تليتْ";
                    const lang = currentLanguage as 'ar' | 'fr' | 'en';
                    return hadith[lang]?.text || hadith.ar.text;
                  })()}
                </p>
              </motion.div>
            </div>
            
            {/* Indicateurs de navigation */}
            <div style={{
              position: 'absolute',
              top: windowWidth < 768 ? '10px' : '15px',
              right: windowWidth < 768 ? '10px' : '15px',
              display: 'flex',
              gap: windowWidth < 768 ? '6px' : '8px',
              zIndex: 3
            }}>
              {aboutImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  style={{
                    width: windowWidth < 768 ? '10px' : '12px',
                    height: windowWidth < 768 ? '10px' : '12px',
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: index === currentImageIndex ? accent : 'rgba(255,255,255,0.5)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                />
              ))}
            </div>
          </div>
        </motion.div>
      </section>

      {/* ── Section Événements (pleine largeur, masquée si vide) ── */}
      {landingLoaded && getLandingEvents(currentLanguage).length > 0 && (
        <section id="events" style={getEventSectionBg(currentLanguage)}>
          {getLandingEvents(currentLanguage).map((ev, idx) => {
            // C) Titre : HTML ou texte brut
            const titleIsHtml = ev.title ? /<[a-z][\s\S]*>/i.test(ev.title) : false;
            // D) Description : HTML ou texte brut
            const descIsHtml  = ev.description ? /<[a-z][\s\S]*>/i.test(ev.description) : false;

            return (
              <motion.div
                key={ev.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.08 }}
              >
                {/* C) Titre */}
                {ev.title && (
                  titleIsHtml
                    ? <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(ev.title) }} />
                    : <h2 style={{ textAlign: 'center', fontSize: '2.5rem', color: 'rgb(69, 90, 100)', margin: 0 }}>{ev.title}</h2>
                )}

                {/* D) Description */}
                {ev.description && (
                  descIsHtml
                    ? <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(ev.description) }} />
                    : <p style={{ textAlign: 'center', fontSize: windowWidth < 768 ? '1rem' : '1.1rem', lineHeight: '1.8', color: '#6C757D', margin: '16px auto 0', maxWidth: 800 }}>{ev.description}</p>
                )}

                {ev.detail_html && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setModalContent({ title: ev.title || '', html: ev.detail_html! })}
                    style={{
                      display: 'block',
                      margin: '20px auto 0',
                      padding: '12px 28px',
                      fontSize: '1rem',
                      borderRadius: 5,
                      border: `2px solid ${primaryColor}`,
                      background: 'transparent',
                      color: primaryColor,
                      cursor: 'pointer',
                    }}
                  >
                    {t('readMore')}
                  </motion.button>
                )}
              </motion.div>
            );
          })}
        </section>
      )}

      {/* ── Séparateur Events → Projects (visible uniquement si la section events est active) ── */}
      {landingLoaded && getLandingEvents(currentLanguage).length > 0 && (
        <div style={{ height: '1px', backgroundColor: '#E2E8F0', margin: '0 40px' }} />
      )}

      {/* Projects Section */}
      <section id="projects" style={{ 
        padding: windowWidth < 768 ? '60px 20px' : '80px 40px', 
        backgroundColor: '#F8F9FA', 
        textAlign: 'center' 
      }}>
        <h2 style={{ 
          fontSize: windowWidth < 768 ? '2rem' : '2.5rem', 
          color: primaryColor, 
          marginBottom: '50px' 
        }}>
          {getSectionTitle('project', currentLanguage, t('projectsTitle'))}
        </h2>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: windowWidth < 768 ? '1fr' : windowWidth < 1024 ? 'repeat(auto-fit, minmax(280px, 1fr))' : 'repeat(auto-fit, minmax(300px, 1fr))', 
          gap: windowWidth < 768 ? '20px' : '30px', 
          maxWidth: '1200px', 
          margin: '0 auto' 
        }}>
          {(landingLoaded
            ? getLandingItems('project', currentLanguage)
            : [1, 2, 3, 4].map(i => ({
                id: -i,
                section_type: 'project' as const,
                lang: currentLanguage as any,
                sort_order: i,
                title: t(`project${i}Title`),
                description: t(`project${i}Desc`),
                detail_html: null,
                image_url: null,
                is_visible: 1 as const,
              }))
          ).map((item, idx) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: idx * 0.1 }}
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '10px',
                boxShadow: '0 5px 20px rgba(0,0,0,0.08)',
                padding: windowWidth < 768 ? '20px' : '30px',
                textAlign: 'center',
                overflow: 'hidden',
              }}
            >
              {resolveImageUrl(item.image_url) && (
                <img
                  src={resolveImageUrl(item.image_url)!}
                  alt={item.title || ''}
                  style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 8, marginBottom: 16 }}
                />
              )}
              <h3 style={{ 
                fontSize: windowWidth < 768 ? '1.5rem' : '1.8rem', 
                color: '#2C3E50', 
                marginBottom: '15px' 
              }}>
                {item.title}
              </h3>
              <div
                style={{ fontSize: windowWidth < 768 ? '0.9rem' : '1rem', color: '#6C757D', marginBottom: '20px', lineHeight: '1.6' }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.description || '') }}
              />
              {item.detail_html && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setModalContent({ title: item.title || '', html: item.detail_html! })}
                style={{
                  padding: windowWidth < 768 ? '8px 16px' : '10px 20px',
                  fontSize: windowWidth < 768 ? '0.8rem' : '0.9rem',
                  borderRadius: '5px',
                  border: `1px solid ${primaryColor}`,
                  backgroundColor: 'transparent',
                  color: primaryColor,
                  cursor: 'pointer',
                }}
              >
                {t('readMore')}
              </motion.button>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {/* Statistics Section */}
      <section id="statistics" style={{ 
        padding: windowWidth < 768 ? '60px 20px' : '80px 40px', 
        backgroundColor: surface, 
        textAlign: 'center' 
      }}>
        <h2 style={{ 
          fontSize: windowWidth < 768 ? '2rem' : '2.5rem', 
          color: primaryColor, 
          marginBottom: '50px' 
        }}>
          {t('statsTitle')}
        </h2>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: windowWidth < 768 ? 'repeat(2, 1fr)' : windowWidth < 1024 ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', 
          gap: windowWidth < 768 ? '20px' : '30px', 
          maxWidth: '1200px', 
          margin: '0 auto' 
        }}>
          <StatItem target={statistics.students} label={t('statStudents')} />
          <StatItem target={statistics.teachers} label={t('statTeachers')} />
          <StatItem target={statistics.circles} label={t('statCircles')} />
          <StatItem target={statistics.parents} label={t('statParents')} />
        </div>
      </section>

      {/* Services Section */}
      <section id="services" style={{ 
        padding: windowWidth < 768 ? '60px 20px' : '80px 40px', 
        backgroundColor: '#F8F9FA', 
        textAlign: 'center' 
      }}>
        <h2 style={{ 
          fontSize: windowWidth < 768 ? '2rem' : '2.5rem', 
          color: primaryColor, 
          marginBottom: '50px' 
        }}>
          {getSectionTitle('service', currentLanguage, t('servicesTitle'))}
        </h2>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: windowWidth < 768 ? '1fr' : windowWidth < 1024 ? 'repeat(auto-fit, minmax(280px, 1fr))' : 'repeat(auto-fit, minmax(300px, 1fr))', 
          gap: windowWidth < 768 ? '20px' : '30px', 
          maxWidth: '1200px', 
          margin: '0 auto' 
        }}>
          {(landingLoaded
            ? getLandingItems('service', currentLanguage)
            : [1, 2, 3, 4].map(i => ({
                id: -i,
                section_type: 'service' as const,
                lang: currentLanguage as any,
                sort_order: i,
                title: t(`service${i}Title`),
                description: t(`service${i}Desc`),
                detail_html: null,
                image_url: null,
                is_visible: 1 as const,
              }))
          ).map((item, idx) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: idx * 0.1 }}
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '10px',
                boxShadow: '0 5px 20px rgba(0,0,0,0.08)',
                padding: windowWidth < 768 ? '20px' : '30px',
                textAlign: 'center',
                overflow: 'hidden',
              }}
            >
              {resolveImageUrl(item.image_url) && (
                <img
                  src={resolveImageUrl(item.image_url)!}
                  alt={item.title || ''}
                  style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 8, marginBottom: 16 }}
                />
              )}
              <h3 style={{ 
                fontSize: windowWidth < 768 ? '1.5rem' : '1.8rem', 
                color: '#2C3E50', 
                marginBottom: '15px' 
              }}>
                {item.title}
              </h3>
              <div
                style={{ fontSize: windowWidth < 768 ? '0.9rem' : '1rem', color: '#6C757D', marginBottom: '20px', lineHeight: '1.6' }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.description || '') }}
              />
              {item.detail_html && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setModalContent({ title: item.title || '', html: item.detail_html! })}
                style={{
                  padding: windowWidth < 768 ? '8px 16px' : '10px 20px',
                  fontSize: windowWidth < 768 ? '0.8rem' : '0.9rem',
                  borderRadius: '5px',
                  border: `1px solid ${primaryColor}`,
                  backgroundColor: 'transparent',
                  color: primaryColor,
                  cursor: 'pointer',
                }}
              >
                {t('readMore')}
              </motion.button>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {/* Contact Section — compact */}
      <section id="contact" style={{ 
        padding: windowWidth < 768 ? '20px 16px' : '24px 40px',
        backgroundColor: '#FFFFFF',
        borderTop: '1px solid #F1F5F9',
      }}>
        <div style={{ maxWidth: '700px', margin: '0 auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
          {/* Titre compact à gauche */}
          <h2 style={{ fontSize: windowWidth < 768 ? '1rem' : '1.1rem', color: primaryColor, fontWeight: '800', margin: 0 }}>
            {t('contact')}
          </h2>

          {/* Infos + bouton sur une ligne */}
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            {[
              { icon: '✉️', text: t('email') },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '5px', backgroundColor: '#F8F9FA', borderRadius: '6px', padding: '6px 10px', border: '1px solid #E5E7EB' }}>
                <span style={{ fontSize: '0.95rem' }}>{item.icon}</span>
                <span style={{ fontSize: '0.78rem', color: '#4B5563' }}>{item.text}</span>
              </div>
            ))}

            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              onClick={() => setShowContactModal(true)}
              style={{ backgroundColor: primaryColor, color: primaryText, border: 'none', borderRadius: '6px', padding: '7px 16px', fontSize: '0.82rem', fontWeight: '700', cursor: 'pointer', fontFamily: 'Cairo, sans-serif' }}
            >
              ✉️ {t('contactUs')}
            </motion.button>
          </div>
        </div>
      </section>

      {/* Contact Modal */}
      {showContactModal && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 2000, backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
          onClick={() => setShowContactModal(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            style={{ backgroundColor: '#FFF', borderRadius: '16px', padding: windowWidth < 768 ? '20px 16px' : '28px 32px', maxWidth: '540px', width: '100%', maxHeight: '90vh', overflowY: 'auto', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}
            onClick={e => e.stopPropagation()}
          >
            {/* En-tête modale */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '1.2rem', fontWeight: '800', color: '#1F2937', margin: 0 }}>{t('contactFormTitle')}</h3>
              <button onClick={() => setShowContactModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#9CA3AF', lineHeight: 1 }}>✕</button>
          </div>

            {contactSubmitted ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>✅</div>
                <h4 style={{ fontSize: '1.2rem', fontWeight: '800', color: '#065F46', marginBottom: '8px' }}>{t('contactSuccess')}</h4>
                <p style={{ color: '#047857', marginBottom: '16px' }}>{t('contactSuccessMsg')}</p>
                {contactSubmittedId && (
                  <div style={{ backgroundColor: '#D1FAE5', borderRadius: '8px', padding: '8px 16px', marginBottom: '16px', display: 'inline-block' }}>
                    <span style={{ fontSize: '12px', color: '#065F46' }}>{t('contactRefLabel')}: </span>
                    <strong style={{ color: '#065F46' }}>#{contactSubmittedId}</strong>
        </div>
                )}
                <button onClick={resetContactForm} style={{ backgroundColor: '#059669', color: '#FFF', border: 'none', borderRadius: '8px', padding: '11px 24px', cursor: 'pointer', fontFamily: 'Cairo, sans-serif', fontWeight: '600' }}>
                  {t('contactSendNew')}
                </button>
              </motion.div>
            ) : (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: windowWidth < 480 ? '1fr' : '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                      {t('contactName')} <span style={{ color: '#DC2626' }}>*</span>
                    </label>
                    <input value={contactName} onChange={e => setContactName(e.target.value)}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '14px', fontFamily: 'Cairo, sans-serif', boxSizing: 'border-box', outline: 'none' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>{t('contactPhone')}</label>
                    <input value={contactPhone} onChange={e => setContactPhone(e.target.value)} dir="ltr"
                      style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '14px', fontFamily: 'Cairo, sans-serif', boxSizing: 'border-box', outline: 'none' }} />
                  </div>
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                    {t('contactEmail')} <span style={{ fontSize: '11px', color: '#9CA3AF', marginInlineStart: '4px' }}>({t('contactPhoneOrEmailHint')})</span>
                  </label>
                  <input value={contactEmail} onChange={e => setContactEmail(e.target.value)} dir="ltr"
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '14px', fontFamily: 'Cairo, sans-serif', boxSizing: 'border-box', outline: 'none' }} />
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                    {t('contactSubject')} <span style={{ color: '#DC2626' }}>*</span>
                  </label>
                  <input value={contactSubject} onChange={e => setContactSubject(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '14px', fontFamily: 'Cairo, sans-serif', boxSizing: 'border-box', outline: 'none' }} />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                      {t('contactMessage')} <span style={{ color: '#DC2626' }}>*</span>
                    </label>
                    <span style={{ fontSize: '11px', color: '#9CA3AF' }}>{contactMessage.length}/2000</span>
                  </div>
                  <textarea value={contactMessage} onChange={e => setContactMessage(e.target.value)} rows={4} maxLength={2000}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '14px', fontFamily: 'Cairo, sans-serif', boxSizing: 'border-box', outline: 'none', resize: 'vertical' }} />
                </div>

                {contactError && (
                  <div style={{ backgroundColor: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', padding: '10px 14px', marginBottom: '12px', color: '#DC2626', fontSize: '13px' }}>
                    {contactError}
                  </div>
                )}

                <motion.button
                  whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                  onClick={handleContactSubmit} disabled={contactLoading}
                  style={{ backgroundColor: primaryColor, color: primaryText, border: 'none', borderRadius: '10px', padding: '13px 32px', fontSize: '15px', fontWeight: '700', cursor: contactLoading ? 'not-allowed' : 'pointer', opacity: contactLoading ? 0.7 : 1, fontFamily: 'Cairo, sans-serif', width: '100%' }}
                >
                  {contactLoading ? t('contactSending') : t('contactSend')}
                </motion.button>
              </div>
            )}
          </motion.div>
        </div>
      )}

      {/* Modale Info Fondation */}
      {showFoundationInfo && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 3000, backgroundColor: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
          onClick={() => setShowFoundationInfo(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            style={{ backgroundColor: '#1E1A0F', borderRadius: '16px', maxWidth: '480px', width: '100%', maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', border: '1px solid rgba(201,162,40,0.3)', direction: isRTL ? 'rtl' : 'ltr' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Bouton fermer X */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px 12px 0' }}>
              <button onClick={() => setShowFoundationInfo(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#C9A228', fontSize: '1.3rem', lineHeight: 1 }}>✕</button>
            </div>

            {/* Contenu scrollable */}
            <div style={{ overflowY: 'auto', padding: '0 24px 20px', textAlign: 'center', flex: 1 }}>
              <img src="/logo_athar_transp_v1.png" alt={t('foundationName')} style={{ width: '80px', height: '80px', objectFit: 'contain', display: 'block', margin: '0 auto 12px' }} />
              <div style={{ fontSize: '1.15rem', fontWeight: '800', color: '#F59E0B', marginBottom: '4px' }}>{t('foundationName')}</div>
              <div style={{ fontSize: '0.85rem', color: '#C9A228', marginBottom: '14px' }}>{t('foundationModalSubtitle')}</div>
              <div style={{ borderTop: '1px solid rgba(201,162,40,0.2)', marginBottom: '14px' }} />
              <p style={{ fontSize: '0.85rem', color: '#D4C5A0', lineHeight: '1.8', textAlign: isRTL ? 'right' : 'left', whiteSpace: 'pre-line', marginBottom: '16px' }}>{t('foundationModalBody')}</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: isRTL ? 'flex-end' : 'flex-start' }}>
                <span style={{ fontSize: '0.82rem', color: '#C9A228' }}>✉️ {t('foundationEmail')}</span>
                <span style={{ fontSize: '0.82rem', color: '#C9A228' }} dir="ltr">📞 {t('foundationPhone')}</span>
              </div>
            </div>

            {/* Footer fixe */}
            <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(201,162,40,0.2)', display: 'flex', gap: '10px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <button
                onClick={() => { setShowFoundationInfo(false); setShowFoundationCompose(true); }}
                style={{ flex: 1, backgroundColor: '#C9A228', color: '#1E1A0F', border: 'none', borderRadius: '8px', padding: '11px', fontWeight: '800', fontSize: '0.9rem', cursor: 'pointer', fontFamily: 'Cairo, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
              >
                ✉️ {t('foundationEmailBtn')}
              </button>
              <a
                href={`tel:${t('foundationPhone')}`}
                style={{ flex: 1, backgroundColor: '#14532D', color: '#86EFAC', border: 'none', borderRadius: '8px', padding: '11px', fontWeight: '800', fontSize: '0.9rem', cursor: 'pointer', fontFamily: 'Cairo, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', textDecoration: 'none' }}
              >
                📞 {t('foundationCallBtn')}
              </a>
              <button
                onClick={() => setShowFoundationInfo(false)}
                style={{ backgroundColor: 'transparent', color: '#C9A228', border: '1px solid rgba(201,162,40,0.4)', borderRadius: '8px', padding: '11px 16px', fontWeight: '600', fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'Cairo, sans-serif' }}
              >
                {t('foundationClose')}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Modale Composer email fondation */}
      {showFoundationCompose && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 3000, backgroundColor: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
          onClick={() => setShowFoundationCompose(false)}>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            style={{ backgroundColor: '#FFF', borderRadius: '16px', maxWidth: '500px', width: '100%', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', direction: isRTL ? 'rtl' : 'ltr' }}
            onClick={e => e.stopPropagation()}
          >
            {/* En-tête */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid #E5E7EB', backgroundColor: '#1E1A0F' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button onClick={() => { setShowFoundationCompose(false); setShowFoundationInfo(true); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#C9A228', fontSize: '1.2rem' }}>{isRTL ? '→' : '←'}</button>
                <span style={{ fontSize: '1rem', fontWeight: '700', color: '#F59E0B', fontFamily: 'Cairo, sans-serif' }}>✉️ {t('foundationComposeTitle')}</span>
              </div>
              <button onClick={() => setShowFoundationCompose(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#C9A228', fontSize: '1.2rem' }}>✕</button>
            </div>

            {/* Destinataire */}
            <div style={{ padding: '8px 18px', backgroundColor: '#FEF9EC', borderBottom: '1px solid #FDE68A', display: 'flex', gap: '8px', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', fontWeight: '700', color: '#92400E' }}>{t('foundationComposeTo')}</span>
              <span style={{ fontSize: '13px', color: '#78350F', fontWeight: '600' }}>{t('foundationEmail')}</span>
            </div>

            {/* Formulaire */}
            <div style={{ overflowY: 'auto', padding: '16px 18px', flex: 1, textAlign: isRTL ? 'right' : 'left' }}>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#374151', marginBottom: '4px' }}>
                  {t('foundationYourName')} <span style={{ color: '#C9A228' }}>*</span>
                </label>
                <input value={composeName} onChange={e => setComposeName(e.target.value)} placeholder={t('foundationNamePlaceholder')}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '14px', fontFamily: 'Cairo, sans-serif', boxSizing: 'border-box', outline: 'none' }} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#374151', marginBottom: '4px' }}>{t('foundationYourEmail')}</label>
                  <input value={composeEmail} onChange={e => setComposeEmail(e.target.value)} placeholder={t('foundationEmailPlaceholder')} dir="ltr"
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '13px', fontFamily: 'Cairo, sans-serif', boxSizing: 'border-box', outline: 'none' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#374151', marginBottom: '4px' }}>{t('foundationYourPhone')}</label>
                  <input value={composePhone} onChange={e => setComposePhone(e.target.value)} placeholder={t('foundationPhonePlaceholder')} dir="ltr"
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '13px', fontFamily: 'Cairo, sans-serif', boxSizing: 'border-box', outline: 'none' }} />
                </div>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#374151', marginBottom: '4px' }}>{t('foundationSubject')}</label>
                <input value={composeSubject} onChange={e => setComposeSubject(e.target.value)} placeholder={t('foundationSubjectPlaceholder')}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '14px', fontFamily: 'Cairo, sans-serif', boxSizing: 'border-box', outline: 'none' }} />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#374151', marginBottom: '4px' }}>
                  {t('foundationMessage')} <span style={{ color: '#C9A228' }}>*</span>
                </label>
                <textarea value={composeMessage} onChange={e => setComposeMessage(e.target.value)} placeholder={t('foundationMessagePlaceholder')} rows={4}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '14px', fontFamily: 'Cairo, sans-serif', boxSizing: 'border-box', outline: 'none', resize: 'vertical' }} />
              </div>
            </div>

            {/* Boutons */}
            <div style={{ padding: '12px 18px', borderTop: '1px solid #E5E7EB', display: 'flex', gap: '10px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <button onClick={() => setShowFoundationCompose(false)}
                style={{ flex: 1, backgroundColor: '#F3F4F6', color: '#374151', border: 'none', borderRadius: '8px', padding: '11px', fontWeight: '600', fontSize: '0.9rem', cursor: 'pointer', fontFamily: 'Cairo, sans-serif' }}>
                {t('foundationClose')}
              </button>
              <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={handleSendFoundationEmail}
                style={{ flex: 2, backgroundColor: '#C9A228', color: '#1E1A0F', border: 'none', borderRadius: '8px', padding: '11px', fontWeight: '800', fontSize: '0.95rem', cursor: 'pointer', fontFamily: 'Cairo, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                ✉️ {t('foundationSend')}
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Footer — Style 7 : Bande latérale colorée, centré */}
      <footer style={{
        backgroundColor: '#0F172A',
        padding: windowWidth < 768 ? '12px 16px' : '14px 40px',
        fontFamily: 'Cairo, sans-serif',
        borderTop: '1px solid #1E293B',
      }}>
        {/* Conteneur centré */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: windowWidth < 768 ? '12px' : '20px',
          flexWrap: 'wrap',
        }}>
          {/* Bande colorée gauche */}
          <div style={{
            width: '4px',
            height: '52px',
            background: 'linear-gradient(180deg, #F59E0B, #EF4444)',
            borderRadius: '4px',
            flexShrink: 0,
          }} />

          {/* Logo + nom + description */}
          <div style={{ textAlign: windowWidth < 768 ? 'center' : 'right' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px', justifyContent: windowWidth < 768 ? 'center' : 'flex-start' }}>
              <button
                onClick={() => setShowFoundationInfo(true)}
                title={t('foundationName')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', transition: 'opacity 0.2s' }}
                onMouseEnter={e => (e.currentTarget.style.opacity = '0.75')}
                onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
              >
                <img
                  src="/logo_athar_transp_v1.png"
                  alt={t('foundationName')}
                  style={{ width: '28px', height: '28px', objectFit: 'contain', display: 'block' }}
                />
              </button>
              <button
                onClick={() => setShowFoundationInfo(true)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '800', color: '#F59E0B', fontFamily: 'Cairo, sans-serif', padding: 0 }}
              >
                {t('foundationName')}
              </button>
            </div>
            <div style={{ fontSize: '0.72rem', color: '#64748B' }}>
              {t('foundationAbout')}
            </div>
          </div>

          {/* Séparateur vertical */}
          {windowWidth >= 768 && (
            <div style={{ width: '1px', height: '36px', backgroundColor: '#1E293B', flexShrink: 0 }} />
          )}

          {/* Liens contact */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
            <a href={`tel:${t('foundationPhone')}`} dir="ltr"
              style={{ color: '#86EFAC', fontSize: '0.72rem', textDecoration: 'none', backgroundColor: '#1E293B', padding: '5px 12px', borderRadius: '6px', border: '1px solid #334155' }}>
              📞 {t('foundationPhone')}
            </a>
            <a href={`mailto:${t('foundationEmail')}`}
              style={{ color: '#FDE68A', fontSize: '0.72rem', textDecoration: 'none', backgroundColor: '#1E293B', padding: '5px 12px', borderRadius: '6px', border: '1px solid #334155' }}>
              ✉️ {t('foundationEmail')}
            </a>
          </div>

          {/* Bande colorée droite (symétrie) */}
          <div style={{
            width: '4px',
            height: '52px',
            background: 'linear-gradient(180deg, #F59E0B, #EF4444)',
            borderRadius: '4px',
            flexShrink: 0,
          }} />
        </div>

        {/* Copyright */}
        <div style={{ borderTop: '1px solid #1E293B', paddingTop: '6px', fontSize: '0.7rem', color: '#45536B', textAlign: 'center', marginTop: '10px', lineHeight: '1.6' }}>
          © {new Date().getFullYear()} {getName(currentLanguage)} &nbsp;·&nbsp; {t('footerPoweredBy')}
        </div>
      </footer>

      {/* ── Modal "Lire la suite" ──────────────────────────────────────────── */}
      <AnimatePresence>
        {modalContent && (
          <motion.div
            key="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setModalContent(null)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.55)',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16,
            }}
          >
            <motion.div
              key="modal-content"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              onClick={e => e.stopPropagation()}
              style={{
                background: '#fff',
                borderRadius: 14,
                width: '100%',
                maxWidth: 680,
                maxHeight: '85vh',
                overflowY: 'auto',
                padding: windowWidth < 768 ? 20 : 32,
                direction: isRTL ? 'rtl' : 'ltr',
              }}
            >
              {/* En-tête */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <h2 style={{ margin: 0, fontSize: windowWidth < 768 ? '1.2rem' : '1.5rem', color: primaryColor }}>
                  {modalContent.title}
                </h2>
                <button
                  onClick={() => setModalContent(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.4rem',
                    cursor: 'pointer',
                    color: '#999',
                    lineHeight: 1,
                    padding: '2px 8px',
                  }}
                  aria-label="Fermer"
                >
                  ×
                </button>
              </div>
              {/* Corps HTML sanitisé */}
              <div
                style={{ lineHeight: 1.8, color: '#444', fontSize: windowWidth < 768 ? '0.95rem' : '1rem' }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(modalContent.html) }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default App;
